import type { ChartConfig } from "@replo/design-system/components/shadcn/core/chart";
import type { ChartInterval } from "replo-utils/analytics";
import type { AnalyticsQuery } from "schemas/generated/analyticsRead";

import * as React from "react";

import { TZDate } from "@date-fns/tz";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltipContent,
} from "@replo/design-system/components/shadcn/core/chart";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const CHART_COLORS = {
  previousPeriod: "#A5C8FF",
  thisPeriod: "#2563EB",
  labelOutside: "#94A2B8",
};

const chartConfig = {
  previousPeriod: {
    label: "Previous Period",
    color: CHART_COLORS.previousPeriod,
  },
  thisPeriod: {
    label: "This Period",
    color: CHART_COLORS.thisPeriod,
  },
} satisfies ChartConfig;

/**
 * This component is currently used in PageDetails to display the line charts for the deep dive page.
 *
 * As we implement more charts (e.g. on the Analytics Home page), we'll make this component more re-usable, and
 * potentially break it down into multiple components. E.g. we might need to pass as props the colors, or
 * labels for the lines, etc...
 *
 * @author Max 2025-04-17
 */
export const ReploLineChart: React.FC<{
  thisPeriod: number[];
  previousPeriod: number[];
  ranges: AnalyticsQuery["ranges"];
  chartInterval: ChartInterval;
  timeZone: string;
  tooltipValueFormatter?: (value: number) => string;
  className?: string;
}> = ({
  thisPeriod,
  previousPeriod,
  ranges,
  chartInterval,
  timeZone,
  tooltipValueFormatter,
  className,
}) => {
  const lastShownTick = React.useRef<string | null>(null);

  // If there's less than 1 day between start/end, the x-ticks will be hours. Otherwise, they'll be days.
  const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
  const { startDatetime, endDatetime, interval } = ranges.mainRange;
  const isLessThanOneDay = endDatetime - startDatetime <= ONE_DAY_IN_MS;

  /**
   * The isFuture and thisPeriodDotted are used to display the dotted line (to indicate that the date is in the future).
   * The strategy here is that we have 3 lines: 1 solid line for previousPeriod, 1 solid line for thisPeriod (in the past), and 1
   * dotted line for thisPeriod (in the future).
   *
   * 1) For each date (x-axis), check if it's in the future.
   *
   * 2) If it's in future, we set thisPeriod to null (so that the solid line from thisPeriod is not displayed for that date).
   * Instead, we set the value for thisPeriodDotted, which will be used to display the dotted line.
   */
  const data = Array.from({ length: thisPeriod.length }, (_, i) => {
    // Each interval requires a dot in the line, so we move forward, interval by interval, from the startDatetime.
    const date = new TZDate(startDatetime + i * interval, timeZone);
    const isFuture = date > new TZDate(Date.now(), timeZone);

    return {
      date: date.valueOf(),
      previousPeriod: previousPeriod[i],
      thisPeriod: !isFuture ? thisPeriod[i] : null,
      thisPeriodDotted: thisPeriod[i],
    };
  });

  return (
    <ChartContainer
      config={chartConfig}
      className={twMerge("-ml-2 w-full", className)}
    >
      <LineChart
        accessibilityLayer
        data={data.map((item) => ({
          ...item,
          date: new TZDate(item.date, timeZone).toISOString(),
        }))}
        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
      >
        <CartesianGrid stroke="#e5e7eb" horizontal={true} vertical={false} />
        <XAxis
          dataKey="date"
          tickMargin={16}
          tickLine={false}
          tick={{ fill: CHART_COLORS.labelOutside }}
          interval={
            chartInterval === "hour"
              ? "equidistantPreserveStart"
              : "preserveStart"
          }
          stroke="#e5e7eb"
          tickFormatter={(value) => {
            /**
             * No need to use TZDate here, as the value we get is already in the correct timezone. More
             * specifically, it's the value from the LineChart's data, which is the string representation
             * of the date (e.g. "04/17/2025, 10:00:00"). This simply re-formats it.
             */
            const formattedDate = new Date(value).toLocaleString("default", {
              ...(isLessThanOneDay
                ? {
                    hour: "numeric",
                    minute: "numeric",
                    hour12: false,
                  }
                : {
                    month: "numeric",
                    day: "2-digit",
                  }),
            });

            /**
             * NOTE (Max, 2025-04-17): There is a display issue with x-ticks if we're using the "hour" interval for a range that spans multiple days:
             *
             * Given that we strip the time from the date we display, the resulting x-ticks only contain day/month. However, given that the line points
             * are hour-based, we're left with multiple x-ticks that have the same value (e.g. 12/14, as we're not showing the hour). This ensures that we
             * only show the date (day/month) if it hasn't been shown already to avoid duplicate x-ticks (fixes INS-160).
             */
            if (lastShownTick.current === formattedDate) {
              return "";
            }

            lastShownTick.current = formattedDate;
            return formattedDate;
          }}
        />

        <YAxis
          tickLine={false}
          tickMargin={16}
          axisLine={false}
          tick={{ fill: CHART_COLORS.labelOutside }}
        />
        <Tooltip
          content={
            <ChartTooltipContent
              className="bg-white"
              keysToHide={["thisPeriodDotted"]}
              labelFormatter={(value) => {
                // Get the date in the format "4/17/2025 12:00"
                const formattedDate = new Date(value).toLocaleDateString(
                  "en-US",
                  {
                    ...(isLessThanOneDay || chartInterval === "hour"
                      ? {
                          hour: "numeric",
                          minute: "numeric",
                          hour12: false,
                        }
                      : {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        }),
                  },
                );

                // Get the GMT offset for the timezone, which will look like: "GMT-06:00"
                const date = new Date();
                const timeZoneOffset = date
                  .toLocaleString("en-US", {
                    timeZone,
                    timeZoneName: "longOffset",
                  })
                  .split(" ")
                  .pop();

                // Final format will look like: "4/17/2025 12:00 (GMT-06:00)"
                return `${formattedDate} (${timeZoneOffset})`;
              }}
              valueFormatter={(value) =>
                tooltipValueFormatter
                  ? tooltipValueFormatter(value)
                  : value.toString()
              }
            />
          }
        />

        <Line
          type="linear"
          dataKey="previousPeriod"
          stroke={CHART_COLORS.previousPeriod}
          strokeWidth={2}
          dot={false}
        />
        <Line
          type="linear"
          dataKey="thisPeriod"
          stroke={CHART_COLORS.thisPeriod}
          strokeWidth={2}
          dot={false}
        />
        <Line
          type="linear"
          dataKey="thisPeriodDotted"
          stroke={CHART_COLORS.thisPeriod}
          strokeWidth={2}
          dot={false}
          strokeDasharray="5 5"
        />
        <ChartLegend
          content={<ChartLegendContent keysToHide={["thisPeriodDotted"]} />}
        />
      </LineChart>
    </ChartContainer>
  );
};
